import React, { useCallback, useEffect } from 'react';
import { defaultValues as defaultValueExpectCardData } from '../../../page-builder/utils/constants';
import { useForm, FormProvider } from 'react-hook-form';
import { FormData } from '../../../page-builder/interface/checkoutInterface';
import { v4 as uuidv4 } from 'uuid';

import eventEmitter from '../../../page-builder/utils/events';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import { countries } from '../../data/country';
import { storePhoneCode } from '../../utils/helper';
import { setIsVGSFormError } from '../../redux/slices/checkoutSlice';
import { setSubmitSuccess } from '../../redux/slices/commonSlice';

interface FormWrapperProps extends ReduxSelectorProps, ReduxDispatchProps {
  children: React.ReactNode;
  singleStep?: boolean | undefined;
  className?: string;
}

export const FormWrapper: React.FC<FormWrapperProps> = ({
  children,
  useAppSelector,
  useAppDispatch,
  className,
}) => {
  const cartData = useAppSelector(state => state.cart?.cartData);
  const storeData = useAppSelector(state => state.store.storeData);
  const { VGSForm } = useAppSelector(state => state.vgsForm);
  const isEmpty = useCallback((obj: object) => {
    return obj ? Object.keys(obj).length === 0 : true;
  }, []);
  const dispatch = useAppDispatch();

  const handleVgsSubmit = (): Promise<{
    Number: string;
    NameOnCard: string;
    Exp: string;
    CVV: string;
    IsEncrypted: boolean;
  } | null> => {
    return new Promise(resolve => {
      if (!VGSForm) {
        console.error('VGS Form is not initialized');
        resolve(null);
        return;
      }

      VGSForm.submit(
        '/post',
        {},
        (_status, response) => {
          // if (!cartData?.ShippingAddress) {
          //   console.error('ShippingAddress is missing'); // Since we are not saving the shipping address
          //   resolve(null);
          //   return;
          // }

          resolve({
            Number: response.card_number,
            NameOnCard: response.card_holder,
            Exp: response.card_exp,
            CVV: response.card_cvc,
            IsEncrypted: true,
          });
        },
        error => {
          console.error('Submission failed:', error);
          resolve(null); // Use resolve(null) instead of throwing
        },
      );
    });
  };

  const onSubmit = async (data: FormData) => {
    try {
      const start_Time = new Date().toISOString();
      let CardInfo = data?.CardInfo;
      dispatch(setSubmitSuccess(true));
      // vgs form submission if VGSForm is initialized
      if (VGSForm) {
        if (
          VGSForm?.state?.card_cvc?.errors?.length ||
          VGSForm?.state?.card_number?.errors?.length ||
          VGSForm?.state?.card_exp?.errors?.length ||
          VGSForm?.state?.card_holder?.errors?.length
        ) {
          dispatch(setIsVGSFormError(true));
          dispatch(setSubmitSuccess(false));
          return;
        } else {
          dispatch(setIsVGSFormError(false));
        }

        if (storeData.IsCardEncryption) {
          try {
            const data = await handleVgsSubmit();
            if (data) {
              CardInfo = data;
            }
          } catch (error) {
            console.error('Error submitting VGS form:', error);
          }
        }
      }
      const totalPrice = cartData?.TotalAmount;
      const customerPhoneNumber = data.CustomerData.PhoneNumber
        ? `${data.phone_code} ${data.CustomerData.PhoneNumber}`
        : null;
      const smsPhoneNumber = data?.SmsMarketing?.PhoneNumber
        ? `${data.phone_code} ${data.SmsMarketing.PhoneNumber}`
        : null;
      const formValues = {
        ...data,
        CardInfo,
        HasSubscription: cartData?.HasSubscription,
        ShopID: storeData?.ShopID,
        ID: uuidv4(),
        TotalAmount: `${totalPrice}`,
        CustomerData: {
          Email: data.CustomerData.Email,
          PhoneNumber: customerPhoneNumber,
        },
        SmsMarketing: {
          PhoneNumber: smsPhoneNumber,
        },
        Shipping: data.Shipping,
        ShippingMethodID: data.ShippingMethodID,
        BillingAddress:
          data.billing_same_as_shipping || isEmpty(data.BillingAddress)
            ? {
                Address1: data.Shipping.Address1,
                Address2: data.Shipping.Address2,
                City: data.Shipping.City,
                CountryCode: data.Shipping.CountryCode,
                FirstName: data.Shipping.FirstName,
                LastName: data.Shipping.LastName,
                Organization: data.Shipping.Organization,
                ProvinceCode: data.Shipping.ProvinceCode,
                Zip: data.Shipping.Zip,
              }
            : { ...data.BillingAddress },
        completePerformancePayload: {
          cart_id: cartData?.ID,
          email: data.CustomerData.Email,
          complete_api_call_time: new Date().toISOString(),
          complete_click_time: start_Time,
        },
      };
      eventEmitter.emit('completePurchase', formValues);
    } catch (error) {
      console.error('Error in form submission:', error);
      // Handle error appropriately, e.g., show a notification to the user
    }
  };

  const getDefaultFormValues = useCallback((): FormData => {
    const defaultValues: FormData = {
      ...defaultValueExpectCardData,
      CardInfo: {
        Number: '',
        Exp: '',
        CVV: '',
      },
    };
    if (
      cartData &&
      cartData.UpdatedAt &&
      new Date().getTime() - new Date(cartData.UpdatedAt).getTime() <
        24 * 60 * 60 * 1000
    ) {
      const phoneNumber = cartData.PhoneNumber || '';
      let filteredPhoneNumber = phoneNumber;

      // Check if phone number starts with any country code (with +)
      for (const country of countries) {
        const prefixedCode = `+${country?.phoneCode}`;
        if (phoneNumber.startsWith(prefixedCode)) {
          filteredPhoneNumber = phoneNumber.slice(prefixedCode.length);
          storePhoneCode('+' + country?.phoneCode || '+1');
          break;
        }
      }

      return {
        ...defaultValues,
        CustomerData: {
          ...defaultValues.CustomerData,
          Email: cartData?.Email || '',
          PhoneNumber: filteredPhoneNumber || '',
        },
        Shipping: {
          ...defaultValues.Shipping,
          Address1: cartData?.ShippingAddress?.Address1 || '',
          Address2: cartData?.ShippingAddress?.Address2 || '',
          City: cartData?.ShippingAddress?.City || '',
          CountryCode: cartData?.ShippingAddress?.CountryCode || '',
          FirstName: cartData?.ShippingAddress?.FirstName || '',
          LastName: cartData?.ShippingAddress?.LastName || '',
          Organization: cartData?.ShippingAddress?.Organization || '',
          ProvinceCode: cartData?.ShippingAddress?.ProvinceCode || '',
          Zip: cartData?.ShippingAddress?.Zip || '',
        },
        ShippingMethodID: cartData?.ShippingMethodID || '',
      };
    }
    return defaultValues;
  }, [
    cartData?.ShippingAddress,
    cartData?.Email,
    cartData?.PhoneNumber,
    cartData?.ShippingMethodID,
  ]);

  const methods = useForm<FormData>({
    defaultValues: getDefaultFormValues(),
    mode: 'onChange',
  });

  useEffect(() => {
    if (cartData) {
      const fieldsToUpdate: (keyof FormData)[] = [
        'CustomerData',
        'Shipping',
        'ShippingMethodID',
      ];
      fieldsToUpdate.forEach(field => {
        methods.setValue(field, getDefaultFormValues()[field], {
          shouldValidate: false,
        });
      });
    }
  }, [methods, getDefaultFormValues]);
  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          // noValidate
          className={className || ''}>
          {children}
        </form>
      </FormProvider>
    </>
  );
};
